import { useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from '../../i18n/useTranslation';
import { Alert } from '../atoms/Alert/Alert';
import { Button } from '../atoms/Button/Button';
import { Select } from '../atoms/Select/Select';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography/Text';
import {
  getQuarantineSensitivity,
  protocolsFor,
  quarantineSensitivityKey,
  startQuarantine,
  type ProtocolType,
  type QuarantineProtocol,
} from '../../services/quarantineService';
import { TreatmentSensitivityNotice } from './TreatmentSensitivityNotice';
import { taskTitles } from './quarantineTitles';
import './Quarantine.css';

export interface QuarantineAnimal {
  readonly id: string;
  readonly name: string;
  readonly category: 'coral' | 'fish' | 'invertebrate' | 'other';
}

/**
 * Choose and start a protocol for one animal (#1826). Shows what the protocol
 * does, its confidence and — for medication — that the dose comes from the
 * product label only.
 */
export function StartQuarantineForm({ aquariumId, animal, onStarted, onCancel, cancelLabel }: {
  aquariumId: string;
  animal: QuarantineAnimal;
  onStarted: (protocol: QuarantineProtocol) => void;
  onCancel: () => void;
  cancelLabel?: string;
}) {
  const { t } = useTranslation();
  const options = protocolsFor(animal.category);
  const [type, setType] = useState<ProtocolType>(options[0]!);
  const [dipInterval, setDipInterval] = useState('14');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<'conflict' | 'generic' | null>(null);
  // #1830: copper / CP sensitivity of fish; a failed lookup simply shows nothing.
  const sensitivity = useQuery({
    queryKey: quarantineSensitivityKey(aquariumId, animal.id),
    queryFn: () => getQuarantineSensitivity(aquariumId, animal.id),
    enabled: animal.category === 'fish',
    retry: false,
  });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      onStarted(
        await startQuarantine(aquariumId, {
          protocol_type: type,
          livestock_id: animal.id,
          ...(type === 'coral' ? { dip_interval_days: Number(dipInterval) } : {}),
          task_titles: taskTitles(t, type, animal.name),
        }),
      );
    } catch (err: unknown) {
      setError((err as { status?: number }).status === 409 ? 'conflict' : 'generic');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate>
      <Stack gap="4">
        {error && <Alert>{t(error === 'conflict' ? 'quarantine.start.conflict' : 'quarantine.start.error')}</Alert>}
        <Select
          id="quarantine-protocol"
          label={t('quarantine.start.protocol')}
          value={type}
          options={options.map((p) => ({ value: p, label: String(t(`quarantine.protocols.${p}.name`)) }))}
          onChange={(e) => setType(e.target.value as ProtocolType)}
          disabled={saving}
        />
        <Text data-testid="quarantine-protocol-summary">{t(`quarantine.protocols.${type}.summary`)}</Text>
        {type === 'ttm' && <Alert tone="warning" live="off">{t('quarantine.protocols.ttm.caveat')}</Alert>}
        <TreatmentSensitivityNotice sensitivities={sensitivity.data ?? []} copperProtocol={type === 'copper'} live="polite" />
        {(type === 'copper' || type === 'coral') && <Text color="muted" variant="caption">{t('quarantine.noDoses')}</Text>}
        {type === 'coral' && (
          <Select
            id="quarantine-dip-interval"
            label={t('quarantine.start.dipInterval')}
            value={dipInterval}
            options={['12', '13', '14', '15', '16'].map((d) => ({ value: d, label: String(t('quarantine.start.days', { days: d })) }))}
            onChange={(e) => setDipInterval(e.target.value)}
            disabled={saving}
          />
        )}
        <div className="quarantine__actions">
          <Button variant="secondary" type="button" onClick={onCancel} disabled={saving}>
            {cancelLabel ?? t('quarantine.cancel')}
          </Button>
          <Button type="submit" isLoading={saving}>{t('quarantine.start.submit')}</Button>
        </div>
      </Stack>
    </form>
  );
}
